import { useState, type ReactNode, type ComponentType } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, LogOut, ChevronDown, ChevronUp } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '@elka/theme';
import { MobileDrawer } from './MobileDrawer';

type IconProps = { size?: number; className?: string; strokeWidth?: number };

export interface NavItemConfig {
  to: string;
  icon: ComponentType<IconProps>;
  label: string;
  subItems?: { to: string; icon: ComponentType<IconProps>; label: string }[];
}

export interface UserConfig {
  name: string;
  initial: string;
  photoURL?: string | null;
}

export interface UserAction {
  icon: ComponentType<IconProps>;
  label: string;
  onClick: () => void;
  className?: string;
}

export interface NavigationDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Callback when the drawer should close */
  onClose: () => void;
  /** Title displayed in the header */
  title?: string;
  /** Additional content in header (e.g., section switcher) */
  headerContent?: ReactNode;
  /** Extra content rendered above the user card in the footer (e.g., theme toggle) */
  footerContent?: ReactNode;
  /** Navigation items */
  items: NavItemConfig[];
  /** User information for the footer card */
  user?: UserConfig;
  /** Action buttons shown in the user card area */
  userActions?: UserAction[];
  /** Sign out callback */
  onSignOut?: () => void;
  /** Extra content inside the expanded user card, under the action buttons (e.g. a settings toggle). */
  userPanel?: ReactNode;
  /** Side the drawer slides from */
  side?: 'right' | 'left';
  /** Calm-v2 styling: flat surface, token colors, no gradient header. */
  calm?: boolean;
  /** Logo + name + subtitle shown in the header instead of the plain title. */
  brand?: { logoSrc: string; title: string; subtitle?: string };
}

/**
 * A shared navigation drawer component with soft brand-tinted styling.
 * Features:
 * - Light background with soft brand tint
 * - Dark text on light background
 * - White cards for active items
 * - Accordion support for sub-items
 * - User profile card and logout button
 */
export function NavigationDrawer({
  isOpen,
  onClose,
  title = 'תפריט',
  headerContent,
  footerContent,
  items,
  user,
  userActions,
  onSignOut,
  userPanel,
  side = 'right',
  calm = false,
  brand,
}: NavigationDrawerProps) {
  const { colors } = useTheme();
  // ponytail: two literal class sets keyed by `calm`; the legacy set is untouched.
  const ui = calm
    ? {
        header: 'bg-surface border-line text-ink border-b !py-3 !px-4',
        bg: 'bg-surface',
        closeBtn: 'rounded-control text-ink-2 hover:bg-surface-soft p-2 transition-colors',
        title: 'text-ink ml-auto text-xl font-extrabold text-balance',
        item: 'rounded-control flex items-center gap-3 px-4 py-3 text-base font-bold transition-[background-color,color]',
        itemActive: 'bg-brand-soft text-brand',
        itemIdle: 'text-ink-2 hover:bg-surface-soft',
        iconActive: 'text-brand',
        iconIdle: 'text-muted',
        sub: 'rounded-control flex items-center gap-3 px-4 py-2.5 text-base font-semibold transition-[background-color,color]',
        footer: 'border-line space-y-3 border-t p-4',
        userCard: 'rounded-card border-line overflow-hidden border bg-surface',
        userPress: 'active:bg-surface-soft',
        avatar: 'bg-brand',
        userName: 'text-ink truncate text-lg font-extrabold',
        chevron: 'text-muted',
        actionsRow: 'border-line flex gap-2 border-t px-2 py-2',
        panel: 'border-line border-t pt-3',
        action: 'text-ink-2 hover:bg-surface-soft',
        signOut: 'rounded-control text-danger hover:bg-danger-soft',
      }
    : {
        header: `bg-gradient-to-r ${colors.headerBg} text-white !py-3 !px-4`,
        bg: 'bg-gradient-to-b from-white to-[#F4F6FA]',
        closeBtn: 'rounded-lg p-2 transition-colors hover:bg-white/20',
        title: 'ml-auto text-xl font-bold text-balance',
        item: 'flex items-center gap-3 rounded-xl px-4 py-3 font-medium transition-[background-color,color,box-shadow]',
        itemActive: 'bg-white font-semibold text-[#0C1A55] shadow-sm',
        itemIdle: 'text-gray-700 hover:bg-white hover:shadow-sm',
        iconActive: 'text-[#3C5FA8]',
        iconIdle: 'text-gray-400',
        sub: 'flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-[background-color,color,box-shadow]',
        footer: 'space-y-3 border-t border-gray-200 p-4',
        userCard: 'overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm',
        userPress: 'active:bg-gray-50',
        avatar: `bg-gradient-to-br ${colors.primaryGradient}`,
        userName: 'truncate text-lg font-bold text-gray-900',
        chevron: 'text-gray-400',
        actionsRow: 'flex gap-2 border-t border-gray-100 px-2 py-2',
        panel: 'border-t border-gray-100 pt-3',
        action: 'text-gray-600 hover:bg-gray-100',
        signOut: 'rounded-xl text-red-500 hover:bg-red-50',
      };
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [isUserExpanded, setIsUserExpanded] = useState(false);
  const expandable = (userActions && userActions.length > 0) || !!userPanel;
  // Collapse the user card on close so the drawer reopens tidy.
  const handleClose = () => {
    setIsUserExpanded(false);
    onClose();
  };

  const toggleExpanded = (label: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const isItemActive = (item: NavItemConfig) => {
    if (item.subItems) {
      return item.subItems.some((sub) => location.pathname === sub.to);
    }
    return location.pathname === item.to;
  };

  return (
    <MobileDrawer
      isOpen={isOpen}
      onClose={handleClose}
      position={side}
      header={
        <div className="space-y-3">
          <div className="flex h-16 items-center md:h-20">
            <button onClick={handleClose} className={ui.closeBtn} aria-label="סגור תפריט">
              <Menu size={24} />
            </button>
            {brand ? (
              <div className="ml-auto flex items-center gap-3">
                <img
                  src={brand.logoSrc}
                  alt=""
                  className="h-10 w-10 rounded-[10px] object-contain"
                />
                <div className="min-w-0 text-right">
                  <h2
                    className={
                      calm
                        ? 'text-ink text-lg leading-tight font-extrabold'
                        : 'text-lg leading-tight font-bold'
                    }
                  >
                    {brand.title}
                  </h2>
                  {brand.subtitle && (
                    <p
                      className={
                        calm ? 'text-muted text-sm font-semibold' : 'text-sm text-white/80'
                      }
                    >
                      {brand.subtitle}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <h2 className={ui.title}>{title}</h2>
            )}
          </div>
          {headerContent}
        </div>
      }
      headerClassName={ui.header}
      bgClassName={ui.bg}
      footer={
        footerContent || user || onSignOut ? (
          <div className={ui.footer}>
            {footerContent}
            {user && (
              <div className={ui.userCard}>
                <button
                  type="button"
                  onClick={expandable ? () => setIsUserExpanded((prev) => !prev) : undefined}
                  className={clsx(
                    'flex w-full items-center gap-3 px-3 py-3',
                    expandable && `cursor-pointer transition-colors ${ui.userPress}`
                  )}
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.name}
                      referrerPolicy="no-referrer"
                      className="h-11 w-11 flex-shrink-0 rounded-full object-cover shadow-lg outline outline-2 -outline-offset-2 outline-black/10"
                    />
                  ) : (
                    <div
                      className={`h-11 w-11 rounded-full ${ui.avatar} flex flex-shrink-0 items-center justify-center text-lg font-bold text-white shadow-lg`}
                    >
                      {user.initial}
                    </div>
                  )}
                  <p className={ui.userName}>{user.name}</p>
                  {expandable && (
                    <ChevronUp
                      size={18}
                      className={clsx(
                        `mr-auto ${ui.chevron} transition-transform duration-200`,
                        !isUserExpanded && 'rotate-180'
                      )}
                    />
                  )}
                </button>
                {isUserExpanded && userPanel && <div className={ui.panel}>{userPanel}</div>}
                {isUserExpanded && userActions && userActions.length > 0 && (
                  <div className={ui.actionsRow}>
                    {userActions.map((action) => (
                      <button
                        key={action.label}
                        onClick={() => action.onClick()}
                        className={clsx(
                          'flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-[background-color,color,transform] hover:scale-[1.02] active:scale-[0.96]',
                          action.className || ui.action
                        )}
                      >
                        <action.icon size={18} />
                        <span>{action.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {onSignOut && (
              <button
                onClick={() => {
                  handleClose();
                  onSignOut();
                }}
                className={`flex w-full items-center justify-center gap-2 px-4 py-3 font-semibold transition-[background-color,transform] active:scale-[0.96] ${ui.signOut}`}
              >
                <LogOut size={20} className="scale-x-[-1]" />
                <span>התנתק</span>
              </button>
            )}
          </div>
        ) : undefined
      }
    >
      <nav className="flex-1 space-y-2 p-4">
        {items.map((item) => {
          const hasSubItems = !!item.subItems;
          const isExpanded = expandedItems.has(item.label);
          const isActive = isItemActive(item);

          if (!hasSubItems) {
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={handleClose}
                className={clsx(ui.item, isActive ? ui.itemActive : ui.itemIdle)}
              >
                <item.icon size={20} className={isActive ? ui.iconActive : ui.iconIdle} />
                <span>{item.label}</span>
              </NavLink>
            );
          }

          return (
            <div key={item.label} className="space-y-1">
              <button
                type="button"
                onClick={() => toggleExpanded(item.label)}
                className={clsx('w-full', ui.item, isActive ? ui.itemActive : ui.itemIdle)}
              >
                <item.icon size={20} className={isActive ? ui.iconActive : ui.iconIdle} />
                <span className="flex-1 text-right">{item.label}</span>
                <ChevronDown
                  size={18}
                  className={clsx(
                    'transition-transform duration-200',
                    isExpanded && 'rotate-180',
                    isActive ? ui.iconActive : ui.iconIdle
                  )}
                />
              </button>

              {isExpanded && (
                <div className="space-y-1 pr-4">
                  {item.subItems!.map((subItem) => {
                    const isSubActive = location.pathname === subItem.to;
                    return (
                      <NavLink
                        key={subItem.to}
                        to={subItem.to}
                        onClick={handleClose}
                        className={clsx(ui.sub, isSubActive ? ui.itemActive : ui.itemIdle)}
                      >
                        <subItem.icon
                          size={18}
                          className={isSubActive ? ui.iconActive : ui.iconIdle}
                        />
                        <span>{subItem.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </MobileDrawer>
  );
}

NavigationDrawer.displayName = 'NavigationDrawer';
